import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  viewChild,
  WritableSignal
} from '@angular/core';
import { select, Store } from '@ngxs/store';
import { filter, get } from 'lodash';
import {
  DatalistItemComponent,
  TooltipComponent,
  VehicleDisclosuresIndicatorComponent
} from 'remarketing-ui-components-lib';
import {
  Address,
  AddVehicleUuidToSelections,
  AuctionItemBuyer,
  BookProviderService,
  BookValueSpreadProviderName,
  BuyingDealer,
  ConfigurationState,
  DateUtilsService,
  EditPostingService,
  FeeDto,
  InventoryItemDetail,
  InventoryManagementArbitrationCase,
  InventoryManagementAuctionItemStatus,
  InventoryManagementFeeDetail,
  InventoryManagementSearchResult,
  InventoryManagementSellerOfferStatus,
  InventoryManagementState,
  InventoryService,
  LaunchDarklyService,
  RemoveVehicleUuidFromSelections,
  SellerRemitDto,
  SellState,
  SettlementBatchService,
  StatusFilter,
  ToggleStateService,
  UpdateReturnToInventoryPageConfiguration,
  UpdateShouldMaintainPreviousSearchInStateConfiguration,
  UserProfile, UserProfileState,
  VehicleToggleState
} from 'remarketing-angular-library';
import { BehaviorSubject, merge } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { getLocalDeliveryString } from 'src/app/shared/utils/util';
import { Router, RouterLink } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  AnalyticsService,
  CurrencyFormatService,
  FormatPhoneNumberPipe,
  NameTruncatePipe,
  SaComponent,
  TelephoneLinkPipe
} from 'smartauction-frontend-micro-library';
import { EditPostingUtilsService } from 'src/sell/lib/posting/services/edit-posting-utils.service';
import { AuctionSummaryConfigurationService } from 'src/auction-summary/services/auction-summary-configuration/auction-summary-configuration.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { DynamicBannerAlert, DynamicBannerAlertType } from 'src/shared/models/dynamic-banner-alert.model';
import { NgxPrintModule } from 'ngx-print';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { CommonModule } from '@angular/common';
import { AuctionSummaryResultItemBannerComponent } from '../../../shared/arbitration/components/auction-summary-result-item-banner/auction-summary-result-item-banner.component';
import { StateIndicatorComponent } from '../../../shared/components/state-indicator/state-indicator.component';
import { ErrorModalComponent, ModalPortalDirective, SaveCommentModalComponent } from '@ally/remarketing/shared/ui';
import { FeeToolTipService } from '../../../shared/services/feetooltip/feetooltip.service';
import { FeeTooltipFormatterService } from '../../../app/shared/fee-tooltip-formatter/fee-tooltip-formatter.service';
import { ImagesCountComponent } from '../../../sell/lib/sell-shared/components/images-count/images-count.component';
import { ActionStatus } from '../../enums/inventory-management.enum';
import { InventoryManagementOfferList } from 'remarketing-angular-library/interfaces/inventory-management.interface';
import { RemovalHistoryComponent } from '../removal-history/removal-history.component';
import { PurchaseHistoryComponent } from '../purchase-history/purchase-history.component';
import { toObservable } from '@angular/core/rxjs-interop';
import { SearchFilterService } from '../../services/search-filter/search-filter.service';
import { VehicleToggleService } from '../../services/vehicle-toggle/vehicle-toggle.service';
import { OfferFilterService } from '../../services/offer-filter/offer-filter.service';
import { SharedlistsService } from '../../../shared/services/sharedlists/sharedlists.service';
import { VehiclesService } from 'src/vehicles/services/vehicle-detail/vehicles.service';

@Component({
  selector: 'app-vehicle-card',
  templateUrl: './vehicle-card.component.html',
  styleUrls: ['./vehicle-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ErrorModalComponent,
    TranslocoDirective,
    AuctionSummaryResultItemBannerComponent,
    RouterLink,
    ImagesCountComponent,
    NgxPrintModule,
    CollapseModule,
    StateIndicatorComponent,
    TooltipComponent,
    CommonModule,
    FormatPhoneNumberPipe,
    TelephoneLinkPipe,
    NameTruncatePipe,
    DatalistItemComponent,
    VehicleDisclosuresIndicatorComponent,
    ModalPortalDirective,
    AuctionSummaryResultItemBannerComponent,
    ImagesCountComponent,
    RemovalHistoryComponent,
    PurchaseHistoryComponent
  ]
})
export class VehicleCardComponent extends SaComponent implements OnInit {
  protected readonly StatusFilter = StatusFilter;
  readonly inputAuctionItemSell$ = input.required<InventoryManagementSearchResult>();

  readonly offerLists$ = input.required<InventoryManagementOfferList[]>();
  readonly searchStatusFilter$ = input.required<StatusFilter>();
  readonly userProfile$ = input.required<UserProfile>();
  readonly index$ = input.required<number>();
  readonly tenantName$ = input.required<string>();
  readonly tenantUuid$ = input.required<string>();
  readonly currentPage$ = input.required<number>();
  readonly auctionItemBuyer$ = input<AuctionItemBuyer>();
  readonly isMobileLayout$ = input<boolean>();
  readonly isServiceFeeVisible$ = input<boolean>();
  readonly isDesktop$ = input<boolean>();
  readonly singleListSelected$ = input<boolean>();
  readonly sellerCommentsEnabled$ = input<boolean>();
  readonly inputVehicleSelectionsEnabled$ = input<boolean>();
  readonly isFilterByInServiceVehicles$ = input(false);
  readonly isIsvFileProcessing$ = input(false);
  readonly isDirty$ = input(false);

  _inTransit$:  WritableSignal<boolean> = linkedSignal(() => this.inputAuctionItemSell$().inTransit);

  _vehicleSelectionsEnabled$ = computed(() => {
    if(this.inServiceVehiclesEnabled) {
      return this.inputVehicleSelectionsEnabled$() && this.teamRedSelectionsEnabled;
    } else {
      return this.inputVehicleSelectionsEnabled$();
    }
  });

  auctionItemSell$ = computed(() => ({
    ...this.inputAuctionItemSell$(),
    inTransit: this._inTransit$()
  }));

  currentListType$ = computed(() => {
    return this.inServiceVehiclesEnabled && !this.isFilterByInServiceVehicles$() ?
      this.vehicleCardTranslations.customOffer : this.vehicleCardTranslations.sharedList;
  });

  offerListTooltipTitle$ = computed(() => {
    return this.isFilterByInServiceVehicles$() ?
      this.vehicleCardTranslations.customOfferTooltipTitle : this.vehicleCardTranslations.sharedListTooltipTitle;
  });

  latestOfferByOfferList$ = computed(() => {
    return this.offerFilterService.filterLatestOfferByOfferList(this.auctionItemSell$());
  });

  readonly expandFocusTarget$ = viewChild<ElementRef<HTMLSpanElement>>('expandFocusTarget');
  readonly modalHost$ = viewChild.required<ModalPortalDirective>(ModalPortalDirective);
  readonly errorModal$ = viewChild<ErrorModalComponent>(ErrorModalComponent);
  readonly print$ = viewChild.required<ElementRef>('print');

  // Keeping arrivedVehicles$ as observable for now due to side effects in usage
  protected readonly arrivedVehicles$ = toObservable(select(InventoryManagementState.arrivedVehicles));
  protected readonly inventoryCurrentStatus$ = select(InventoryManagementState.currentStatus);
  protected readonly selectedVehicles$ = select(VehicleToggleState.selectedVehicles);
  protected readonly processingVehicles$ = select(InventoryManagementState.processingVehicles);

  readonly showProcessingBanner$ = computed(() => {
    this.processingVehicles$();
    return this.store.selectSignal(
      InventoryManagementState.isVehicleInProcessing(this.auctionItemSell$().vehicleUuid)
    )();
  });

  protected readonly isVehicleSelected$ = computed(() => {
    return this.selectedVehicles$().some(
      (vehicle: InventoryManagementSearchResult) => vehicle.vehicleUuid === this.auctionItemSell$().vehicleUuid
    );
  });

  private isCollapsed$$ = new BehaviorSubject(true);
  isCollapsed: boolean;

  groundedSubject = new BehaviorSubject<boolean>(false); // To manage the grounded state.
  groundedValue = this.groundedSubject.asObservable(); // To update and manage the view.

  private changeDetectorRef: ChangeDetectorRef;
  protected store: Store;
  readonly toggleState: ToggleStateService;
  private readonly bookProviderService: BookProviderService;
  protected readonly _analyticsService: AnalyticsService;
  private readonly tooltipService: FeeToolTipService;
  protected readonly launchDarklyService: LaunchDarklyService;
  private readonly editPostingUtilsService: EditPostingUtilsService;
  private readonly editPostingService: EditPostingService;
  private readonly router: Router;
  private readonly inventoryService: InventoryService;
  private readonly translocoService: TranslocoService;
  private readonly dateUtilService: DateUtilsService;
  private readonly settlementBatchService: SettlementBatchService;
  private readonly feeTooltipFormatterService: FeeTooltipFormatterService;
  private readonly currencyFormatService: CurrencyFormatService;
  private readonly auctionSummaryConfigurationService: AuctionSummaryConfigurationService;
  protected bsModalService: BsModalService;
  private readonly vehicleToggleService: VehicleToggleService;
  private readonly searchFilterService: SearchFilterService;
  private readonly offerFilterService: OfferFilterService;
  private readonly sharedListsService: SharedlistsService;
  private readonly vehicleService: VehiclesService;

  private readonly statusMap = {
    [ActionStatus.PENDING_SELLER_ACTION]: { text: 'Pending Seller Action', variant: 'danger' },
    [ActionStatus.PENDING_BUYER_ACTION]: { text: 'Pending Buyer Action', variant: 'dark' },
    [ActionStatus.NO_ACTION]: { text: 'No Action', variant: 'success' },
    [ActionStatus.HOLD]: { text: 'Hold', variant: 'grey' },
    [ActionStatus.REMOVED]: { text: 'Removed', variant: 'removed' },
    [ActionStatus.EXPIRED]: { text: 'Removed', variant: 'removed' }
  };

  commentsModalInstance: SaveCommentModalComponent;
  editingComment = false;
  imgUrlLarge: string;
  vdpCommentName = 'VDP Comment';

  id = this.generateId();
  spreadToBook: number;
  openArbitrationRequest = 'Open Arbitration Request';
  linkEvent = 'link';

  showAuctionStartTime = false;
  showAuctionEndTime = false;
  showAuctionExtendTime = false;
  showAuctionStartTimePending = false;

  bookValues: Array<any> = [];
  bannerAlerts = [];

  showSoldHistory = false;
  showRemovedHistory;
  showViewOffers: boolean;
  isOpenArbitration = false;
  claimNumber = 0;

  textBiddingInProgress: string;
  textBiddingEnded: string;
  singleFeeLabel: string;
  singleFeeAmount: number;
  tooltipContent: any;
  holdReasonList = ['N/A'];

  editCutoffErrorMessage: string;
  offerListTooltipBody = '';

  sellerRemitDto: SellerRemitDto = {} as SellerRemitDto;

  bannerTranslations = {
    textProcessingVehicleStatus: '',
    textVehicleArrived: '',
    textBsoBanner: ''
  };

  vehicleCardTranslations = {
    feesTooltipAriaLabel: '',
    saleFeesTooltipAriaLabel: '',
    distanceUnit: '',
    textTotalPrice: '',
    buyerFeesLabel: '',
    sellerFeesLabel: '',
    labelArbitration: '',
    textOpenRequest: '',
    labelArbitrationClosed: '',
    textCase: '',
    textCaseOpened: '',
    textInspectionSeriesLabel: '',
    customOffer: '',
    currentListType: '',
    sharedListTooltipTitle: '',
    customOfferTooltipTitle: '',
    ariaLabelSelectAdd: '',
    stockNumber: '',
    ariaLabelExpandCard: '',
    ariaLabelCollapseCard: '',
    altCollapse: '',
    altExpand: '',
    withinNinetyDays: '',
    textMailTitle: '',
    textBuyerInfo: '',
    textStartDate: '',
    textEndDate: '',
    textPending: '',
    textHighestOffer: '',
    textOffers: '',
    textQuestions: '',
    textEdit: '',
    textSaleDate: '',
    textSaleFee: '',
    textSalePrice: '',
    ariaLabelViewHistory: '',
    textRemovedDate: '',
    textViewPostingHistory: '',
    textRepost: '',
    textRepostedOn: '',
    textLocation: '',
    textDaysRemaining: '',
    textOpeningPrice: '',
    textCurrentBid: '',
    textOdometer: '',
    textHits: '',
    textBuyNowPrice: '',
    textAverageSalesPrice: '',
    textTenantBadge: '',
    textDealershipContact: '',
    ariaLabelCall: '',
    textBuyerEmail: '',
    textBuyerContact: '',
    textBuyerAuctionAccess: '',
    textBuyerFinanceSource: '',
    textCustomerId: '',
    textSeller: '',
    textSettlementDate: '',
    textBidCount: '',
    textFees: '',
    textDelivery: '',
    textTenantBadgeLowerCase: '',
    textSellerUpperCase: '',
    textVehicleSummary: '',
    textVin: '',
    textStockUnit: '',
    textVehiclePrice: '',
    textSaleDateTime: '',
    textAuction: '',
    textProvider: '',
    textMailTitleTo: '',
    textTitleContactName: '',
    textTitleContactEmail: '',
    textTitleContactPhone: '',
    textBuyerInfoUpper: '',
    textBuyerContactName: '',
    NA: '',
    partOf: '',
    sharedList: '',
    ariaLabelPrintVehicle: '',
    textOnHold: '',
    textSalesInformation: '',
    textBuyerTaxes: '',
    textBuyerTaxesSub: '',
    textPriceFederalTaxLabel: '',
    textPriceStateTaxLabel: '',
    textFeesFederalTaxLabel: '',
    textFeesStateTaxLabel: '',
    textSellerFeesAndTaxes: '',
    labelMyComment: '',
    textDaysOnHold: '',
    textPostingPrice: '',
    textHoldReason: '',
    textHoldReasons: '',
    textRemovalReason: '',
    textPostedDate: '',
    textBidListAdds: '',
    textRemovals: '',
    textHighestOfferPrice: '',
    textHistoricalHigh: '',
    historicalHighDescription: '',
    labelDisclosures: '',
    arealabelRedlight: '',
    toolTipLetterRedlight: ''
  };

  listViewTableTranslations = {
    textVehicle: '',
    textOffers: '',
    textQuestions: '',
    textStatus: '',
    textHighestOffer: '',
    textBuyNow: '',
    textOpeningPrice: '',
    textSaleFee: '',
    textSalePrice: '',
    textTotalPrice: '',
    textStockUnitNumber: '',
    textAccept: '',
    textCounter: '',
    textDecline: '',
    textWithdraw: '',
    textRevive: '',
    textViewHistory: '',
    textEdit: '',
    textClose: '',
    textRepost: '',
    textViewPostingHistory: '',
    textEditListingGround: '',
    textUnreserve: '',
    textReserve: '',
    textInspectionSeriesLabel: '',
    textVin: '',
    textChangeReservedDealership: ''
  };

  commonTranslations = {
    labelBulkSale: ''
  };

  sellerActionMapCounter = new Map<string, number>([
    ['ACCEPT', 0],
    ['DECLINE', 0],
    ['WITHDRAW', 0],
    ['COUNTER', 0],
    ['REVIVE', 0]
  ]);

  showArrivedBanner = false;
  localTimeZoneNameExtended: string;
  disclosureToolTipRed: any;
  conditionIndicatorTranslations: {
    yellowTitle: '';
    yellowIconLetter: '';
  };
  formattedSpreadToBookLabels: { bookProviderName: string; bookProviderValue: string; } = {
    bookProviderName: '',
    bookProviderValue: ''
  };

  get manageInventoryMaxSelections(): number {
    return this.launchDarklyService.getFeatureFlag('sell-manageInventoryMaxSelections', -1);
  }

  constructor() {
    super();
    this.changeDetectorRef = inject(ChangeDetectorRef);
    this.store = inject(Store);
    this.toggleState = inject(ToggleStateService);
    this.bookProviderService = inject(BookProviderService);
    this._analyticsService = inject(AnalyticsService);
    this.tooltipService = inject(FeeToolTipService);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.editPostingUtilsService = inject(EditPostingUtilsService);
    this.editPostingService = inject(EditPostingService);
    this.router = inject(Router);
    this.inventoryService = inject(InventoryService);
    this.translocoService = inject(TranslocoService);
    this.dateUtilService = inject(DateUtilsService);
    this.settlementBatchService = inject(SettlementBatchService);
    this.feeTooltipFormatterService = inject(FeeTooltipFormatterService);
    this.currencyFormatService = inject(CurrencyFormatService);
    this.auctionSummaryConfigurationService = inject(AuctionSummaryConfigurationService);
    this.bsModalService = inject(BsModalService);
    this.vehicleToggleService = inject(VehicleToggleService);
    this.searchFilterService = inject(SearchFilterService);
    this.offerFilterService = inject(OfferFilterService);
    this.sharedListsService = inject(SharedlistsService);
    this.vehicleService = inject(VehiclesService);
    this.bookValues = this.bookProviderService.bookValues;
  }

  getOfferListVehicles() {
    this.offerListTooltipBody = this.sharedListsService.offerListTooltipBody(this.offerLists$());
  }

  get isSellerArbitrationEnabled(): boolean {
    return this.auctionSummaryConfigurationService.isSellerArbitrationEnabled;
  }

  get offerListsTooltip() {
    return this.sharedListsService.offerListsTooltip(this.offerListTooltipTitle$(), this.offerListTooltipBody);
  }

  get isOfferListsTooltipEmpty(): boolean {
    return this.sharedListsService.isOfferListsTooltipBodyEmpty(this.offerListTooltipBody);
  }

  get title() {
    return `${this.detail('modelYear')} ${this.detail('make')} ${this.detail('model')} ${this.detail('trim')} ${
      this.driveTrain
    } `;
  }

  detail(key: keyof InventoryItemDetail): string {
    return get(this.itemDetails, key, '');
  }

  get itemDetails(): any | {} {
    return get(this.auctionItemSell$(), 'itemDetail', {});
  }

  get driveTrain() {
    return get(this.auctionItemSell$(), 'itemDetail.powerTrain.driveTrainAbbrev', '');
  }

  get storageLocationCityState(): string {
    return this.auctionItemSell$()
            && this.getAuctionItemSellProp('storageLocation.state', false)
            && this.getAuctionItemSellProp('storageLocation.city', false)
      ? this.getAuctionItemSellProp('storageLocation.city', false) + ', '
                + this.getAuctionItemSellProp('storageLocation.state', false)
      : this.vehicleCardTranslations.NA;
  }

  getAuctionItemSellProp(path: string, otherwise: any = this.vehicleCardTranslations.NA): string {
    return get(this.auctionItemSell$(), path, otherwise);
  }

  get currentAmount(): number {
    return this.auctionItemSell$()?.currentAmount;
  }

  get openOfferCount(): number {
    return this.auctionItemSell$()?.offers?.length ? this.getAuctionItemOfferCount : 0;
  }

  get getAuctionItemOfferCount(): number {
    return this.auctionItemSell$()?.openOfferCount ? this.auctionItemSell$().openOfferCount : 0;
  }

  get highestOffer(): number {
    return this.auctionItemSell$()?.highestOffer ? this.auctionItemSell$().highestOffer : undefined;
  }

  get openQuestionCount(): number {
    return this.auctionItemSell$()?.openQuestionCount ? this.auctionItemSell$().openQuestionCount : 0;
  }

  get bookProvider(): string {
    return `${this.formattedSpreadToBookLabels.bookProviderName} ${this.formattedSpreadToBookLabels.bookProviderValue}`;
  }

  get bidStatus() {
    if (
      this.auctionItemSell$() &&
      !this.auctionItemSell$().openingPrice &&
      this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.ACTIVE
    ) {
      if (this.auctionItemSell$().auctionWindow) {
        return this.textBiddingInProgress;
      } else {
        return this.textBiddingEnded;
      }
    } else {
      return undefined;
    }
  }

  get showDeliveryField(): boolean {
    return this.launchDarklyService.getFeatureFlag('inventory-deliveryEnabled', true);
  }

  get status() {
    // Note: We may need to adjust this method more if SIM V2 is sending back actionStatus where V1 was not.
    // The old logic assumes actionStatus will not always exist on response and fallback is auctionItemStatus

    let actionOrVehicleStatus = this.getAuctionItemSellProp('actionStatus', this.getAuctionItemSellProp('auctionItemStatus', ''));

    // If specific filter selected and/or other conditions are met, override the above result
    if(this.inventoryCurrentStatus$() === StatusFilter.REMOVED_VEHICLES
       || this.inventoryCurrentStatus$() === StatusFilter.HOLD_VEHICLES
       || (this.inventoryCurrentStatus$() === StatusFilter.ALL_VEHICLES && this.isRemovedExpiredOrHold)
    ) {
      actionOrVehicleStatus = this.getAuctionItemSellProp('auctionItemStatus', '');
    }

    return this.statusMap[actionOrVehicleStatus] || { text: '', variant: '' };
  }

  get isRemovedExpiredOrHold() : boolean {
    return this.auctionItemSell$()?.auctionItemStatus === this.vehicleStatusFilter.REMOVED
        || this.auctionItemSell$()?.auctionItemStatus === this.vehicleStatusFilter.EXPIRED
        || this.auctionItemSell$()?.auctionItemStatus === this.vehicleStatusFilter.HOLD;
  }

  get statusChangedDate(): string {
    return this.dateUtilService.localizeDateAndTime(this.auctionItemSell$().statusChangedDate, true);
  }

  get datePosted(): string {
    return this.dateUtilService.localizeDateAndTime(this.getAuctionItemSellProp('postedDate'), true);
  }

  get auctionStartTime(): string {
    return this.dateUtilService.localizeDateAndTime(this.auctionItemSell$()?.auctionStartTime, true);
  }

  get auctionEndTime(): string {
    return this.dateUtilService.localizeDateAndTime(this.auctionItemSell$()?.auctionEndTime, true);
  }

  get auctionExtendedTime(): string {
    const extendedTime = this.dateUtilService.localizeDateAndTime(this.auctionItemSell$()?.auctionExtendEndTime, true);
    if (extendedTime !== '') {
      return extendedTime + this.localTimeZoneNameExtended;
    } else {
      return extendedTime;
    }
  }

  get isTenantBadgeValue(): boolean {
    return !!this.auctionItemSell$()?.auctionTenant?.length;
  }

  get tenantBadgeValue(): string {
    return this.auctionItemSell$()?.auctionTenant ? this.auctionItemSell$().auctionTenant : this.vehicleCardTranslations.NA;
  }

  get saleDateTime(): string {
    return this.dateUtilService.localizeDateAndTime(this.auctionItemSell$().saleDate, true);
  }

  get isProceedsDisplay(): boolean {
    return (
      this.isReleaseProceedsEnabled &&
      (this.auctionItemSell$()?.holdProceedsTypeCode === 'R' || this.auctionItemSell$()?.holdProceedsTypeCode === 'H')
    );
  }

  get releasedDate(): string {
    if (this.auctionItemSell$().proceedsReleasedDate === undefined || this.auctionItemSell$().proceedsReleasedDate == null) {
      return '';
    }
    return this.dateUtilService.localizeDateAndTime(this.auctionItemSell$().proceedsReleasedDate, true);
  }

  get settlementDateValue(): string {
    return this.auctionItemSell$()?.settlementDate?.length
      ? this.dateUtilService.localizeDate(this.auctionItemSell$().settlementDate)
      : this.vehicleCardTranslations.NA;
  }

  get localDeliveryValue(): string {
    return getLocalDeliveryString(this.auctionItemSell$().localDelivery);
  }

  get getSellerName(): string {
    return (this.auctionItemSell$()?.seller && this.auctionItemSell$()?.seller?.dealerName)
      ? this.auctionItemSell$().seller.dealerName
      : this.vehicleCardTranslations.NA;
  }

  get platformTenantName() {
    return get(this.store.selectSnapshot(ConfigurationState.configuration), 'tenantFullName', '');
  }

  get platformAddress() {
    return get(this.store.selectSnapshot(ConfigurationState.configuration), 'platform.address', '');
  }

  get platformProvider() {
    const name = get(this.store.selectSnapshot(ConfigurationState.configuration), 'platform.name', '');
    const quebecReg = get(
      this.store.selectSnapshot(ConfigurationState.configuration),
      'platform.quebecRegistrationNumber',
      ''
    );
    const federalTaxId = get(this.store.selectSnapshot(ConfigurationState.configuration), 'platform.federalTaxId', '');
    return `${name} (Business #: ${federalTaxId}; Quebec Registration #: ${quebecReg})`;
  }

  get getRemovalReason() {
    const removalReason =
      this.isItemStatusReasons && this.auctionItemSell$()?.itemStatusReasons[0]?.reason?.length
        ? this.auctionItemSell$().itemStatusReasons[0].reason
        : '';

    const removalBy =
      this.isItemStatusReasons && this.auctionItemSell$()?.itemStatusReasons[0]?.updatedBy?.length
        ? `(${this.auctionItemSell$().itemStatusReasons[0].updatedBy})`
        : '';

    return removalReason.concat(removalBy).length === 0 ? this.vehicleCardTranslations.NA : `${removalReason} ${removalBy}`;
  }

  get isItemStatusReasons(): boolean {
    return !!this.auctionItemSell$()?.itemStatusReasons?.length;
  }

  get arbitrationClaimRoute(): string {
    return this.isOpenArbitration
      ? `/arbitration/${this.auctionItemSell$().vehicleUuid}/${this.claimNumber}`
      : `/arbitration/${this.auctionItemSell$().vehicleUuid}/new`;
  }

  setArbitrationClaim() {
    if (this.auctionItemSell$()?.arbitrationCases?.length) {
      this.auctionItemSell$().arbitrationCases.forEach(arbitrationCase => {
        if (arbitrationCase.status === 'OPEN') {
          this.isOpenArbitration = true;
          this.claimNumber = arbitrationCase.claimNumber;
          this.bannerAlerts.push(
            new DynamicBannerAlert({
              type: DynamicBannerAlertType.ARBITRATION_CASE,
              alertContent: {
                ...arbitrationCase,
                link: this.arbitrationClaimRoute
              }
            } as DynamicBannerAlert)
          );
        }
      });
    }
  }

  // constructs the auctionItem reasons array
  getHoldReason() {
    if (
      this.auctionItemSell$()
      && this.auctionItemSell$()?.auctionItemStatus === this.vehicleStatusFilter.HOLD
      && !!this.auctionItemSell$()?.itemStatusReasons?.length
    ) {
      // filtering reason only if statusCleared false
      const nonClearedStatuses = [...this.auctionItemSell$().itemStatusReasons].filter(r => !r.statusCleared);
      // Lower legacyReasonId means it was created earlier
      nonClearedStatuses.sort((r1, r2) => (r1.legacyReasonId < r2.legacyReasonId ? -1 : 1));
      // mapping only reasons array
      const reasonList = nonClearedStatuses.map(item => {
        return item?.reason?.length ? item.reason : this.vehicleCardTranslations.NA;
      });
      this.holdReasonList = reasonList.length ? reasonList : this.holdReasonList;
    }
  }

  /**
   * AuctionItem Title Address Detail
   */
  titleAddress(key: keyof Address): string {
    return get(this.titleAddressValue, key, '');
  }

  get titleAddressValue(): any | {} {
    return get(this.auctionItemSell$(), 'titleAddress', {});
  }

  get titleAddressName(): string {
    const nameValue = this.titleAddress('name') ? this.titleAddress('name') : this.vehicleCardTranslations.NA;
    return nameValue;
  }

  get titleAddressDetail(): string {
    const address1 = this.titleAddress('addressLine1') ? this.titleAddress('addressLine1') : this.vehicleCardTranslations.NA;
    return address1;
  }

  get titleAddressDetail2(): string {
    const address2 = this.titleAddress('addressLine2') ? this.titleAddress('addressLine2') : '';
    return address2;
  }

  get titleAddressRegion(): string {
    const city = this.titleAddress('city') ? this.titleAddress('city') : this.vehicleCardTranslations.NA;
    const state = this.titleAddress('state') ? this.titleAddress('state') : this.vehicleCardTranslations.NA;
    const zip = this.titleAddress('zip') ? this.titleAddress('zip') : this.vehicleCardTranslations.NA;
    return `${city}, ${state} ${zip}`;
  }

  get isTitleContactEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-showTitleContactEnabled', false);
  }

  get titleContactInfo(): { name?: string; email?: string; phone?: string } | null {
    const childTitleContact = get(this.auctionItemSell$(), 'buyerDetails.contactDetails.title', null);
    if (childTitleContact?.name || childTitleContact?.email || childTitleContact?.phone) {
      return childTitleContact;
    }
    return get(this.auctionItemSell$(), 'leadBuyerDetails.contactDetails.title', null);
  }

  get titleContactName(): string {
    return this.titleContactInfo?.name || this.vehicleCardTranslations.NA;
  }

  get titleContactEmail(): string {
    return this.titleContactInfo?.email || this.vehicleCardTranslations.NA;
  }

  get titleContactPhone(): string | null {
    return this.titleContactInfo?.phone || null;
  }

  /**
   * AuctionItem buyer info detail
   */
  buyerDetails(key: keyof AuctionItemBuyer) {
    return get(this.buyerDetailsValue, key, '');
  }

  get buyerDetailsValue(): any | {} {
    return get(this.auctionItemSell$(), 'buyerDetails', {});
  }

  get buyerDetailsPhysicalAddress(): Address {
    return this.buyerDetails('physicalAddress');
  }

  get buyerContactDetails(): any {
    return get(this.auctionItemSell$(), 'buyerDetails.contactDetails.buyer', {});
  }

  get buyerDetailsName(): string {
    return this.buyerDetails('legalName') || this.vehicleCardTranslations.NA;
  }

  get buyerDetailsAddress(): string {
    return this.buyerDetailsPhysicalAddress?.addressLine1
      ? this.buyerDetailsPhysicalAddress.addressLine1
      : this.vehicleCardTranslations.NA;
  }

  get buyerDetailsAddress2(): string {
    return this.buyerDetailsPhysicalAddress?.addressLine2 ? this.buyerDetailsPhysicalAddress.addressLine2 : '';
  }

  get buyerDetailRegion(): string {
    const city = this.buyerDetailsPhysicalAddress?.city ? this.buyerDetailsPhysicalAddress.city : this.vehicleCardTranslations.NA;
    const state = this.buyerDetailsPhysicalAddress?.state ? this.buyerDetailsPhysicalAddress.state : this.vehicleCardTranslations.NA;
    const zip = this.buyerDetailsPhysicalAddress?.zip ? this.buyerDetailsPhysicalAddress.zip : this.vehicleCardTranslations.NA;
    return `${city}, ${state} ${zip}`;
  }

  get buyerDetailPhone(): string | null {
    return this.buyerContactDetails?.phone ? this.buyerContactDetails.phone : null;
  }

  get buyerDetailName(): string | null {
    return this.buyerContactDetails?.name ? this.buyerContactDetails.name : this.vehicleCardTranslations.NA;
  }

  get buyerDetailAuctionAccessId(): string | null {
    return this.buyerContactDetails?.auctionAccessId ? this.buyerContactDetails.auctionAccessId : null;
  }

  get isBuyerEmail(): boolean {
    return !!this.buyerContactDetails?.email?.length;
  }

  get buyerDetailEmail(): string | null {
    return this.buyerContactDetails?.email?.length ? this.buyerContactDetails.email : this.vehicleCardTranslations.NA;
  }

  get buyerDetailsPrimaryPhone(): string | null {
    return this.buyerDetails('primaryPhone') ? this.buyerDetails('primaryPhone') : null;
  }

  get isTenantBadgeEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-tenantBadgeEnabled', false);
  }

  get isRemovedActionsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('menu-sell-removedVariation', 0) !== 0;
  }

  get isHoldActionsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('menu-sell-holdVariation', 0) !== 0;
  }

  get isBuyerDetailsEmailEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('inventory-export-buyerDetailsEnabled', false);
  }

  get customerIdDetail(): any {
    return get(this.auctionItemSell$(), 'customerId', this.vehicleCardTranslations.NA);
  }

  get buyerFinanceSourceDetail(): any {
    return get(this.auctionItemSell$(), 'buyerFinanceSource', this.vehicleCardTranslations.NA);
  }

  get closedArbitrationCases(): InventoryManagementArbitrationCase[] {
    return filter(this.auctionItemSell$().arbitrationCases, { status: 'CLOSED' });
  }

  get isVehicleGounded(): boolean {
    return (
      this.auctionItemSell$().groundedDate !== null &&
      this.auctionItemSell$().groundedDate !== undefined &&
      this.auctionItemSell$().groundedDate.length > 0
    );
  }

  get partOfSharedListClassList() {
    return 'd-flex align-items-center ms-1 ' + (this.openOfferCount > 0 ? 'ms-md-2' : 'ms-md-0');
  }

  initializeBookProviderValueLabels() {
    this.formattedSpreadToBookLabels = this.vehicleService.formatBookProviderLabel(
      this.bookProviderService.defaultBookProviderName,
      this.bookProviderService.defaultBookProviderValue
    );
  }

  ngOnInit() {
    this.initializeBookProviderValueLabels();
    /* istanbul ignore next */
    this.imgUrlLarge = this.getAuctionItemSellProp('itemDetail.image', '');
    this.checkDrawer();

    this.determineAuctionTimeDisplay();
    this.groundedSubject.next(this.isVehicleGounded); // Combine value coming from auctionItemSell into one observable.
    const globalCollapse$ = this.toggleState.isCollapsed$.pipe(
      tap(isCollapsed => this.isCollapsed$$.next(isCollapsed))
    );
    const localCollapse$ = this.isCollapsed$$.asObservable().pipe(
      tap(isCollapsed => {
        this.isCollapsed = isCollapsed;
        this.changeDetectorRef.markForCheck();
      })
    );
    merge(globalCollapse$, localCollapse$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe();

    this.arrivedVehicles$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.store
        .select(InventoryManagementState.isVehicleInArrived(this.auctionItemSell$().vehicleUuid))
        .pipe(takeUntil(this.destroyed$))
        .subscribe(hasArrived => {
          this.showArrivedBanner = hasArrived;
          if (hasArrived && this.auctionItemSell$().inTransit) {
            this._inTransit$.set(false);
          }
          this.changeDetectorRef.markForCheck();
        });
    });
    this.getHoldReason();
    this.setArbitrationClaim();
    this.getTranslations();
    this.getOfferListVehicles();
    this.showAvailableMultiActions();
  }

  getTranslations() {
    const keyParams = {
      shared: {},
      'sellModule.manageInventory': {},
      common: {},
      'offerList.general.offerListToolTipTitle': {},
      'auctionSummary.auctionSummaryResultItem': {},
      UIComponents: {},
      'sellModule.manageInventory.vehicleCard': {},
      'offerList.general': {},
      'search.inventoryItem': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (translation: any[]) => {
          this.vehicleCardTranslations.feesTooltipAriaLabel =
            translation[0].feeTooltipModal.textMoreInformationAriaLabelFeesSold;
          this.vehicleCardTranslations.saleFeesTooltipAriaLabel =
            translation[0].feeTooltipModal.textMoreInformationAriaLabelSaleFeesSold;
          this.vehicleCardTranslations.buyerFeesLabel = translation[0].feeTooltipModal.textBuyerFees;
          this.vehicleCardTranslations.sellerFeesLabel = translation[0].feeTooltipModal.textSellerFees;
          this.vehicleCardTranslations.stockNumber = translation[1].vehicleCard.textStockUnit;

          this.disclosureToolTipRed = translation[1].vehicleCard.disclosureToolTipR;
          this.editCutoffErrorMessage = translation[1].vehicleCard.editCutoffTimeError;
          this.textBiddingInProgress = translation[1].vehicleCard.textBiddingInProgress;
          this.textBiddingEnded = translation[1].vehicleCard.textBiddingEnded;
          this.localTimeZoneNameExtended = translation[2].localTimeZoneNameExtended;
          this.vehicleCardTranslations.distanceUnit = translation[2].distanceUnit;
          this.commonTranslations = translation[2].labelBulkSale;
          this.vehicleCardTranslations.textInspectionSeriesLabel = translation[1].vehicleCard.textInspectionSeries;

          this.vehicleCardTranslations.sharedListTooltipTitle = translation[7].offerListToolTipTitle;
          this.vehicleCardTranslations.customOfferTooltipTitle = translation[7].customOfferToolTipTitle;
          this.vehicleCardTranslations.partOf = translation[7].partOf;
          this.vehicleCardTranslations.sharedList = translation[7].sharedList;
          this.vehicleCardTranslations.customOffer = translation[7].customOffer;

          this.listViewTableTranslations.textHighestOffer = translation[1].listView.textHighestOffer;
          this.listViewTableTranslations.textOpeningPrice = translation[1].listView.textOpeningPrice;
          this.listViewTableTranslations.textSalePrice = translation[1].listView.textSalePrice;
          this.listViewTableTranslations.textSaleFee = translation[1].listView.textSaleFee;
          this.listViewTableTranslations.textStatus = translation[1].listView.textStatus;
          this.listViewTableTranslations.textBuyNow = translation[1].listView.textBuyNow;
          this.listViewTableTranslations.textStockUnitNumber = translation[1].listView.textStockUnitNumber;
          this.listViewTableTranslations.textClose = translation[1].listView.textClose;
          this.listViewTableTranslations.textAccept = translation[1].listView.textAccept;
          this.listViewTableTranslations.textCounter = translation[1].listView.textCounter;
          this.listViewTableTranslations.textDecline = translation[1].listView.textDecline;
          this.listViewTableTranslations.textWithdraw = translation[1].listView.textWithdraw;
          this.listViewTableTranslations.textRevive = translation[1].listView.textRevive;
          this.listViewTableTranslations.textOffers = translation[1].listView.textOffers;
          this.listViewTableTranslations.textQuestions = translation[1].listView.textQuestions;
          this.listViewTableTranslations.textViewHistory = translation[1].listView.textViewHistory;
          this.listViewTableTranslations.textEdit = translation[1].listView.textEdit;
          this.listViewTableTranslations.textRepost = translation[1].listView.textRepost;
          this.listViewTableTranslations.textViewPostingHistory = translation[1].listView.textViewPostingHistory;
          this.listViewTableTranslations.textEditListingGround = translation[1].listView.textEditListingGround;
          this.listViewTableTranslations.textUnreserve = translation[1].listView.textUnreserve;
          this.listViewTableTranslations.textReserve = translation[1].listView.textReserve;
          this.listViewTableTranslations.textInspectionSeriesLabel = translation[1].listView.textInspectionSeriesLabel;
          this.listViewTableTranslations.textVin = translation[1].listView.textVinLabel;
          this.listViewTableTranslations.textChangeReservedDealership = translation[1].listView.textChangeReservedDealership;

          this.vehicleCardTranslations.labelArbitration = translation[4].labelArbitration;
          this.vehicleCardTranslations.textOpenRequest = translation[4].textOpenRequest;
          this.vehicleCardTranslations.labelArbitrationClosed = translation[4].labelArbitrationClosed;
          this.vehicleCardTranslations.textCase = translation[4].textCase;
          this.vehicleCardTranslations.textCaseOpened = translation[4].textCaseOpened;

          this.conditionIndicatorTranslations = translation[5].conditionIndicatorTranslations;

          this.bannerTranslations.textProcessingVehicleStatus = translation[6].textProcessingVehicleStatus;
          this.bannerTranslations.textVehicleArrived = translation[6].textVehicleArrived;
          this.bannerTranslations.textBsoBanner = translation[6].textbsoBanner;
          this.vehicleCardTranslations.ariaLabelExpandCard = translation[6].ariaLabelExpandCard;
          this.vehicleCardTranslations.ariaLabelCollapseCard = translation[6].ariaLabelCollapseCard;
          this.vehicleCardTranslations.altCollapse = translation[6].altCollapse;
          this.vehicleCardTranslations.altExpand = translation[6].altExpand;
          this.vehicleCardTranslations.withinNinetyDays = translation[6].withinNinetyDays;
          this.vehicleCardTranslations.textMailTitle = translation[6].textMailTitle;
          this.vehicleCardTranslations.textBuyerInfo = translation[6].textBuyerInfo;
          this.vehicleCardTranslations.textStartDate = translation[6].textStartDate;
          this.vehicleCardTranslations.textEndDate = translation[6].textEndDate;
          this.vehicleCardTranslations.textPending = translation[6].textPending;
          this.vehicleCardTranslations.textHighestOffer = translation[6].textHighestOffer;
          this.vehicleCardTranslations.textOffers = translation[6].textOffers;
          this.vehicleCardTranslations.textQuestions = translation[6].textQuestions;
          this.vehicleCardTranslations.textEdit = translation[6].textEdit;
          this.vehicleCardTranslations.textSaleDate = translation[6].textSaleDate;
          this.vehicleCardTranslations.textSaleFee = translation[6].textSaleFee;
          this.vehicleCardTranslations.textSalePrice = translation[6].textSalePrice;
          this.vehicleCardTranslations.ariaLabelViewHistory = translation[6].ariaLabelViewHistory;
          this.vehicleCardTranslations.textRemovedDate = translation[6].textRemovedDate;
          this.vehicleCardTranslations.textViewPostingHistory = translation[6].textViewPostingHistory;
          this.vehicleCardTranslations.textRepost = translation[6].textRepost;
          this.vehicleCardTranslations.textRepostedOn = translation[6].textRepostedOn;
          this.vehicleCardTranslations.textLocation =translation[6].textLocation;
          this.vehicleCardTranslations.textDaysRemaining = translation[6].textDaysRemaining;
          this.vehicleCardTranslations.textOpeningPrice = translation[6].textOpeningPrice;
          this.vehicleCardTranslations.textCurrentBid = translation[6].textCurrentBid;
          this.vehicleCardTranslations.textOdometer = translation[6].textOdometer;
          this.vehicleCardTranslations.textHits = translation[6].textHits;
          this.vehicleCardTranslations.textBuyNowPrice = translation[6].textBuyNowPrice;
          this.vehicleCardTranslations.textAverageSalesPrice = translation[6].textAverageSalesPrice;
          this.vehicleCardTranslations.textTenantBadge = translation[6].textTenantBadge;
          this.vehicleCardTranslations.textDealershipContact =translation[6].textDealershipContact;
          this.vehicleCardTranslations.ariaLabelCall = translation[6].ariaLabelCall;
          this.vehicleCardTranslations.textBuyerContactName = translation[6].textBuyerContactName;
          this.vehicleCardTranslations.textBuyerEmail = translation[6].textBuyerEmail;
          this.vehicleCardTranslations.textBuyerContact = translation[6].textBuyerContact;
          this.vehicleCardTranslations.textBuyerAuctionAccess = translation[6].textBuyerAuctionAccess;
          this.vehicleCardTranslations.textBuyerFinanceSource = translation[6].textBuyerFinanceSource;
          this.vehicleCardTranslations.textCustomerId = translation[6].textCustomerId;
          this.vehicleCardTranslations.textSeller = translation[6].textSeller;
          this.vehicleCardTranslations.textSettlementDate = translation[6].textSettlementDate;
          this.vehicleCardTranslations.textBidCount = translation[6].textBidCount;
          this.vehicleCardTranslations.textFees = translation[6].textFees;
          this.vehicleCardTranslations.textDelivery = translation[6].textDelivery;
          this.vehicleCardTranslations.textTenantBadgeLowerCase = translation[6].textTenantBadgeLowerCase;
          this.vehicleCardTranslations.textSellerUpperCase = translation[6].textSellerUpperCase;
          this.vehicleCardTranslations.textVehicleSummary = translation[6].textVehicleSummary;
          this.vehicleCardTranslations.textVin = translation[6].textVin;
          this.vehicleCardTranslations.textStockUnit = translation[6].textStockUnit;
          this.vehicleCardTranslations.textVehiclePrice = translation[6].textVehiclePrice;
          this.vehicleCardTranslations.textSaleDateTime = translation[6].textSaleDateTime;
          this.vehicleCardTranslations.textAuction = translation[6].textAuction;
          this.vehicleCardTranslations.textProvider = translation[6].textProvider;
          this.vehicleCardTranslations.textMailTitleTo = translation[6].textMailTitleTo;
          this.vehicleCardTranslations.textTitleContactName = translation[6].textTitleContactName;
          this.vehicleCardTranslations.textTitleContactEmail = translation[6].textTitleContactEmail;
          this.vehicleCardTranslations.textTitleContactPhone = translation[6].textTitleContactPhone;
          this.vehicleCardTranslations.textBuyerInfoUpper = translation[6].textBuyerInfoUpper;
          this.vehicleCardTranslations.NA = translation[6].NA;
          this.vehicleCardTranslations.ariaLabelSelectAdd = translation[6].ariaLabelSelectAdd;
          this.vehicleCardTranslations.ariaLabelPrintVehicle = translation[6].ariaLabelPrintVehicle;
          this.vehicleCardTranslations.textOnHold = translation[6].textOnHold;
          this.vehicleCardTranslations.textSalesInformation = translation[6].textSalesInformation;
          this.vehicleCardTranslations.textBuyerTaxes = translation[6].textBuyerTaxes;
          this.vehicleCardTranslations.textBuyerTaxesSub = translation[6].textBuyerTaxesSub;
          this.vehicleCardTranslations.textPriceFederalTaxLabel = translation[6].textPriceFederalTaxLabel;
          this.vehicleCardTranslations.textPriceStateTaxLabel = translation[6].textPriceStateTaxLabel;
          this.vehicleCardTranslations.textFeesFederalTaxLabel = translation[6].textFeesFederalTaxLabel;
          this.vehicleCardTranslations.textFeesStateTaxLabel = translation[6].textFeesStateTaxLabel;
          this.vehicleCardTranslations.textSellerFeesAndTaxes = translation[6].textSellerFeesAndTaxes;
          this.vehicleCardTranslations.labelMyComment = translation[6].labelMyComment;
          this.vehicleCardTranslations.textDaysOnHold = translation[6].textDaysOnHold;
          this.vehicleCardTranslations.textPostingPrice = translation[6].textPostingPrice;
          this.vehicleCardTranslations.textHoldReason = translation[6].textHoldReason;
          this.vehicleCardTranslations.textHoldReasons = translation[6].textHoldReasons;
          this.vehicleCardTranslations.textRemovalReason = translation[6].textRemovalReason;
          this.vehicleCardTranslations.textPostedDate = translation[6].textPostedDate;
          this.vehicleCardTranslations.textBidListAdds = translation[6].textBidListAdds;
          this.vehicleCardTranslations.textRemovals = translation[6].textRemovals;
          this.vehicleCardTranslations.textHighestOfferPrice = translation[6].textHighestOfferPrice;
          this.vehicleCardTranslations.textTotalPrice = translation[6].textTotalPrice;
          this.vehicleCardTranslations.textHistoricalHigh = translation[6].historicalHigh;
          this.vehicleCardTranslations.historicalHighDescription = translation[6].historicalHighDescription;

          this.vehicleCardTranslations.labelDisclosures = translation[8].labelDisclosures;
          this.vehicleCardTranslations.arealabelRedlight = translation[8].arealabelRedlight;
          this.vehicleCardTranslations.toolTipLetterRedlight = translation[8].toolTipLetterRedlight;
        },
        error => {
          console.log(error);
        }
      );
  }

  updateBackToInventory() {
    // These have been updated to use Inventory Management State configuration
    this.store.dispatch(new UpdateReturnToInventoryPageConfiguration(this.currentPage$()));
    this.store.dispatch(new UpdateShouldMaintainPreviousSearchInStateConfiguration(true));
  }

  checkDrawer() {
    const drawerUuid = this.store.selectSnapshot(SellState.DrawerStatus);
    if (this.auctionItemSell$() && drawerUuid === this.auctionItemSell$().vehicleUuid) {
      this.showRemovedHistory = true;
    }
  }

  determineAuctionTimeDisplay() {
    if (this.auctionItemSell$()) {
      !this.auctionItemSell$().auctionWindow
        ? this.determineDisplayTimeOutsideOfAuction()
        : this.determineDisplayTimeForOngoingAuctions();
    }
  }

  private determineDisplayTimeForOngoingAuctions() {
    this.auctionItemSell$()?.auctionWindow === 'INPROGRESS'
      ? (this.showAuctionEndTime = true)
      : this.auctionItemSell$()?.auctionWindow === 'EXTENDED'
        ? (this.showAuctionExtendTime = true)
        : (this.showAuctionStartTime = true);
  }

  private determineDisplayTimeOutsideOfAuction() {
    if (this.dateUtilService.isLessThanCurrentDateAndTime(this.auctionItemSell$()?.auctionEndTime)) {
      this.showAuctionStartTimePending = true;
    } else {
      this.showAuctionStartTime = true;
    }
  }

  private static idCounter = 0;

  generateId() {
    return 'vc-' + (++VehicleCardComponent.idCounter).toString(36);
  }

  getSpreadToBookValue() {
    if (this.spreadToBook) {
      return this.spreadToBook;
    }
    if (!this.userProfile$() || !this.userProfile$().userPreferences || !this.auctionItemSell$()?.bookValues) {
      return undefined;
    }
    const defaultBookProvider = this.userProfile$().userPreferences.defaultBookProvider.providerName;
    const defaultProviderValue = this.userProfile$().userPreferences.defaultBookProvider.providerValue;

    let bookProviders = [];

        if (defaultBookProvider === BookValueSpreadProviderName.MMR){
          bookProviders = this.auctionItemSell$().bookValues.filter( bookValue => bookValue.providerName === BookValueSpreadProviderName.MANHEIM )
        }else{
          bookProviders = this.auctionItemSell$().bookValues.filter( bookValue => bookValue.providerName === defaultBookProvider )
        }

    if (bookProviders.length > 0) {
      return this.getCurrentValue(bookProviders, defaultBookProvider, defaultProviderValue);
    }
  }

  getCurrentValue(bookProviders, defaultBookProvider, defaultProviderValue) {
    const filteredProvider = bookProviders[0];
    let providerValue =
      defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.NADA.toLocaleLowerCase() ||
      defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.BLACK_BOOK.toLocaleLowerCase() ||
      defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.MMR.toLocaleLowerCase()
        ? `${defaultProviderValue}`.toLocaleLowerCase()
        : null;

    //using regex for case-insensitive replacement of 'Adjusted ' from provider value for manheim
    if (providerValue && defaultBookProvider.toLocaleLowerCase() === BookValueSpreadProviderName.MMR.toLocaleLowerCase()) {
      providerValue = providerValue.replace(/^adjusted\s+/i, '');
    }

    const filteredBookValues = filteredProvider.values.filter(
      value => value.providerValue.toLocaleLowerCase() === providerValue
    );

    if (filteredBookValues.length > 0) {
      this.spreadToBook = filteredBookValues[0].valueAmount;
      return this.spreadToBook;
    }
  }

  formatCurrency(currency, withCents?) {
    return this.currencyFormatService.formatCurrency(currency, withCents).replaceAll('-', '');
  }

  getBidStatusClasses() {
    let classList = '';
    if (!this.auctionItemSell$()?.openingPrice) {
      if (this.showAuctionExtendTime) {
        classList = 'has-extended-bid-status';
      } else {
        classList = 'has-bid-status';
      }
    }
    if (this.openOfferCount > 0 || this.openQuestionCount > 0) {
      classList += ' has-action-badge';
    }
    return classList;
  }

  getStatusIndicatorClass() {
    switch (this.auctionItemSell$()?.auctionItemStatus) {
    case this.vehicleStatusFilter.SOLD:
    case this.vehicleStatusFilter.ACTIVE:
      return 'three-row-status-indicator';
    case this.vehicleStatusFilter.REMOVED:
      return 'two-row-status-indicator';
    default:
      return null;
    }
  }

  toggleCollapse() {
    this._analyticsService.track('clickEvent', {
      tagName: 'BUTTON',
      allytmln: this.isCollapsed ? 'Expand Vehicle Card' : 'Collapse Vehicle Card',
      linkName: this.isCollapsed ? 'Expand Vehicle Card' : 'Collapse Vehicle Card',
      href: 'javascript:void(0);'
    });

    this.isCollapsed$$.next(!this.isCollapsed$$.getValue());

    setTimeout(() => {
      if (!this.isCollapsed && this.expandFocusTarget$()) {
        this.expandFocusTarget$().nativeElement.focus();
        this.changeDetectorRef.markForCheck();
      }
    }, 1);
  }

  get showTenantServiceFee() {
    return this.isServiceFeeVisible$()
      && this.auctionItemSell$()?.auctionItemTenantUuid === this.tenantUuid$()
      && this.auctionItemSell$()?.buyFee?.fees?.length > 0;
  }

  scrollToHistory(event: MouseEvent) {
    setTimeout(() => {
      const vehicleCardDiv = (event.target as HTMLElement).closest('.card').firstElementChild;
      vehicleCardDiv.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      this.changeDetectorRef.markForCheck();
    }, 300);
  }

  displaySoldHistory(event: MouseEvent) {
    this.showSoldHistory = !this.showSoldHistory;

    if (event.target) {
      this.scrollToHistory(event);
    }
  }

  displayRemovedHistory(event: MouseEvent) {
    this.showRemovedHistory = !this.showRemovedHistory;

    if (event.target) {
      this.scrollToHistory(event);
    }
  }

  getBuyFeeDetailsSizeForPrint(fees: InventoryManagementFeeDetail[]) {
    if (this.isTenantCanadian()) {
      return this.getFeeDetailsSize(this.removeTaxesFromFeesList(fees));
    } else {
      return this.getFeeDetailsSize(fees);
    }
  }

  getSellFeeDetailsSizeForPrint(fees: InventoryManagementFeeDetail[]) {
    return this.getFeeDetailsSize(fees);
  }

  removeTaxesFromFeesList(fees: InventoryManagementFeeDetail[]): InventoryManagementFeeDetail[] {
    return fees.filter(fee => !fee.description.toUpperCase().includes('TAX'));
  }

  // function used for the keyvalue pipe to maintain the order of the fees. Changing default from return 0
  // to emtpy function as return 0 wouldn't work with Angular upgrade.
  returnUndefined() {
    return undefined;
  }

  get isBuyFees(): boolean {
    return !!this.auctionItemSell$()?.buyFee?.fees?.length;
  }

  getFeeDetailsSize(fees: InventoryManagementFeeDetail[]) {
    const feesToTranslate = this.mapInventoryManagementFeesToFeeDto(fees);
    const feesKeyMap = this.feeTooltipFormatterService.getFeeMap(this.tooltipService.translatedFees(feesToTranslate));
    const feeSize = Object.keys(feesKeyMap).length;
    if (fees && feeSize === 1) {
      Object.keys(feesKeyMap).forEach(key => {
        const value = feesKeyMap[key];
        this.singleFeeLabel = key;
        this.singleFeeAmount = value;
      });
    }
    return fees && feeSize;
  }

  auctionItemSellWithRemitData() {
    if (this.isTenantCanadian() && this.auctionItemSell$()?.auctionItemStatus === this.vehicleStatusFilter.SOLD) {
      this.settlementBatchService
        .getSellerRemitDto(this.auctionItemSell$().vehicleUuid)
        .pipe(take(1))
        .subscribe(
          dto => {
            this.sellerRemitDto = dto;
            this.changeDetectorRef.markForCheck();
          },
          error => {
            console.log(error);
          }
        );
    }
  }

  // Used to intercept the print function for ngx-print, as it fetches seller remit data before displaying the print screen
  public triggerPrint(): void {
    this.auctionItemSellWithRemitData();
    setTimeout(() => {
      this.print$().nativeElement.click();
      this.changeDetectorRef.markForCheck();
    }, 500);
  }

  // Marks a given vehicle as selected
  toggleSelected() {
    this._analyticsService.trackEvent(
      'checkbox',
      'Selected ' + this.auctionItemSell$().inventoryItemUuid + ' - ' + !this.isVehicleSelected$()
    );

    const filterByStatusMap = {
      [InventoryManagementAuctionItemStatus.ACTIVE]: StatusFilter.ACTIVE_VEHICLES,
      [InventoryManagementAuctionItemStatus.SOLD]: StatusFilter.SOLD_VEHICLES,
      [InventoryManagementAuctionItemStatus.HOLD]: StatusFilter.HOLD_VEHICLES,
      [InventoryManagementAuctionItemStatus.REMOVED]: StatusFilter.REMOVED_VEHICLES,
      [InventoryManagementAuctionItemStatus.RESERVED]: StatusFilter.RESERVED_VEHICLES,
    };

    if (this.isVehicleSelected$()) {
      this.store.dispatch(new RemoveVehicleUuidFromSelections(this.auctionItemSell$()));
      const vehiclesSelected = this.store.selectSnapshot(VehicleToggleState.selectedVehicles);
      if (this.inventoryCurrentStatus$() === StatusFilter.ALL_VEHICLES && vehiclesSelected.length === 0) {
        this.searchFilterService.setFilterStatus(StatusFilter.ALL_VEHICLES);
        window.requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      }
    } else {
      this.store.dispatch(new AddVehicleUuidToSelections(this.auctionItemSell$()));
      const vehiclesSelected = this.store.selectSnapshot(VehicleToggleState.selectedVehicles);
      if (this.inventoryCurrentStatus$() === StatusFilter.ALL_VEHICLES && vehiclesSelected.length <= 1) {
        this.searchFilterService.setFilterStatus(filterByStatusMap[this.auctionItemSell$().auctionItemStatus], false);
        window.requestAnimationFrame(() => {
          window.scrollTo(0, 0);
        });
      }
    }
    this.vehicleToggleService.evaluateSelectAllCheckbox();
  }

  analyticsInfo(pageDetails: string) {
    this._analyticsService.trackEvent('app-tooltip', pageDetails);
  }

  analyticsClickEvent(name: string, property: string) {
    this._analyticsService.trackEvent(name, property);
  }

  isEditCutOffTime() {
    if (this.isEditDisabled === false) {
      this.inventoryService
        .getEditTimeCutoff(
          this.auctionItemSell$()?.inventoryItemUuid,
          this.editPostingUtilsService.getSelectedDealershipUuid(this.auctionItemSell$()?.seller?.dealerUuid)
        )
        .pipe(take(1))
        .subscribe((cutOffResponse: any) => {
          if (cutOffResponse.isWithinEditCutoffThreshold === true) {
            this.router.navigate(['/inventory/edit/' + this.auctionItemSell$()?.inventoryItemUuid]);
            this.updateBackToInventory();
          } else {
            this.errorModal$().message = this.editCutoffErrorMessage;
            this.errorModal$().btnCloseAlignCenter = true;
            this.errorModal$().show();
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  isTenantCanadian(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  isVinDecodeRequired() {
    const datePosted = new Date(this.getAuctionItemSellProp('postedDate'));
    const currentDate = new Date();
    return this.dateUtilService.getDifferenceInDaysBetweenTwoDates(datePosted, currentDate) > 60;
  }

  onClickRepost(event: Event, vin: string) {
    event.preventDefault();
    if (this.isVinDecodeRequired()) {
      this.editPostingService
        .getVinDecodeDetails(vin, false)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          response => {
            this.router.navigate(['/inventory/repost/', this.auctionItemSell$()?.inventoryItemUuid]);
            this.updateBackToInventory();
          },
          error => {
            this.errorModal$().message = this.getErrorMessage(error, vin);
            this.errorModal$().btnCloseAlignCenter = true;
            this.errorModal$().show();
            this.changeDetectorRef.markForCheck();
          }
        );
    } else {
      this.router.navigate(['/inventory/repost/', this.auctionItemSell$()?.inventoryItemUuid]);
      this.updateBackToInventory();
    }
  }

  showAvailableMultiActions() {
    const userOeUuid = this.store.selectSnapshot(UserProfileState.userProfile).oeUuid;
    if(this.auctionItemSell$()?.offers?.length > 0) {
      this.resetAvailableActionCounter();
      this.auctionItemSell$().offers.forEach(offer => {
        if(offer.latestOffer && userOeUuid && (offer.sellerOeUuid === userOeUuid)) {
          this.showViewOffers = true;
          offer.offerActions.forEach(offerAction => {
            this.sellerActionMapCounter.set(offerAction, this.sellerActionMapCounter.get(offerAction) + 1);
          });
        }
      });
    }
  }
  resetAvailableActionCounter(): void {
    this.sellerActionMapCounter = new Map<string, number>([
      ['ACCEPT', 0],
      ['DECLINE', 0],
      ['WITHDRAW', 0],
      ['COUNTER', 0],
      ['REVIVE', 0]
    ]);
  }

  getErrorMessage(errorRes, vin: string) {
    // @TODO: check to see if this flow still occurs and if we need translations for these messages
    let errorMessage = 'The VIN service is unavailable. Try again later.';
    if (!errorRes.error || !errorRes.error.errors) {
      return errorMessage;
    }
    switch (errorRes.error.errors[0].code) {
    case 'F-REMPLT-IIS-VINACTHLD':
      errorMessage = `<b>VIN ${vin}</b> is on hold or is in active status.`;
      break;
    case 'F-REMPLT-IIS-VINNF':
    case 'F-REMPLT-IIS-INVLDVIN':
      errorMessage = `<b>VIN ${vin}</b> is not found in our service.`;
      break;
    case 'F-REMPLT-IIS-ASTSVCUNAVL':
      errorMessage = 'The VIN service is unavailable. Try again later.';
      break;
    }
    return errorMessage;
  }

  get feeDetailsTooltip() {
    const buyFees = this.mapInventoryManagementFeesToFeeDto(this.auctionItemSell$().buyFee.fees);
    this.tooltipContent = this.tooltipService.getFeeValues(buyFees);
    return {
      title: this.vehicleCardTranslations.buyerFeesLabel,
      metaTitle: this.vehicleCardTranslations.feesTooltipAriaLabel,
      body: this.tooltipContent
    };
  }

  get sellFeeDetailsTooltip() {
    const sellFees = this.mapInventoryManagementFeesToFeeDto(this.auctionItemSell$().sellFee.fees);
    this.tooltipContent = this.tooltipService.getFeeValues(sellFees);
    return {
      title: this.vehicleCardTranslations.sellerFeesLabel,
      metaTitle: this.vehicleCardTranslations.saleFeesTooltipAriaLabel,
      body: this.tooltipContent
    };
  }

  get calculatedTotalPrice(): number {
    const auctionItem = this.auctionItemSell$();
    const salePrice = auctionItem?.salePrice ? auctionItem.salePrice : auctionItem?.offerAmount ?  auctionItem.offerAmount : undefined;
    const buyFeeTotal =   auctionItem?.buyFee?.total ? auctionItem.buyFee.total : undefined;

    return salePrice && buyFeeTotal ? salePrice + buyFeeTotal : undefined;
  }

  mapInventoryManagementFeesToFeeDto(inventoryManagementFees: InventoryManagementFeeDetail[]): FeeDto[] {
    return inventoryManagementFees.map(
      inventoryFee =>
        ({
          description: inventoryFee?.description ? inventoryFee.description : '',
          feeType: inventoryFee?.feeType ? inventoryFee.feeType.toString() : '',
          type: inventoryFee?.type ? inventoryFee.type.toString() : '',
          amount: inventoryFee?.amount ? inventoryFee.amount : 0
        } as FeeDto)
    );
  }

  get buyFeesForPrint() {
    if (this.isTenantCanadian()) {
      return this.feeTooltipFormatterService.getFeeMap(
        this.tooltipService.translatedInventoryManagementFees(
          this.removeTaxesFromFeesList(this.auctionItemSell$().buyFee.fees)
        )
      );
    } else {
      return this.feeTooltipFormatterService.getFeeMap(
        this.tooltipService.translatedInventoryManagementFees(this.auctionItemSell$().buyFee.fees)
      );
    }
  }

  get sellFeesForPrint() {
    return this.feeTooltipFormatterService.getFeeMap(
      this.tooltipService.translatedInventoryManagementFees(this.auctionItemSell$().sellFee.fees)
    );
  }

  get showActionBarCheckbox() {
    if (this.searchStatusFilter$() === StatusFilter.SHARED_VEHICLES) {
      return (
        this.isOfferListSellerSelectAllEnabled &&
        this.singleListSelected$() &&
        this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.ACTIVE &&
        (this.isPendingBuyerAction || this.isPendingSellerAction)
      );
    }

    return ((this.necessarySelectionFlagsPresentByTenant || (this.isSellReportingFeatureEnabled
      && this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.SOLD))
      && (this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.HOLD  ||
        this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.ACTIVE ||
        this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.RESERVED ||
        this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.SOLD)
    );
  }

  get necessarySelectionFlagsPresentByTenant(): boolean {
    if(this.inServiceVehiclesEnabled) {
      return this._vehicleSelectionsEnabled$() && this.teamRedSelectionsEnabled;
    } else {
      return this._vehicleSelectionsEnabled$();
    }
  }

  get teamRedSelectionsEnabled() {
    return this.inServiceVehiclesEnabled && this.isFilterByInServiceVehicles$();
  }

  get disableActionBarCheckbox() {
    if (
      !this.isVehicleSelected$() &&
      this.manageInventoryMaxSelections > -1 &&
      this.store.selectSnapshot(VehicleToggleState.selectedVehicles)?.length >= this.manageInventoryMaxSelections
    ) {
      return true;
    }

    if (this.searchStatusFilter$() === StatusFilter.SHARED_VEHICLES) {
      return this.isOfferListSellerSelectAllEnabled && this.singleListSelected$() && this.isPendingBuyerAction;
    }
    return this.showProcessingBanner$();
  }

  get isPendingSellerAction(): boolean {
    const latestOffer = this.latestOfferByOfferList$();
    if (latestOffer) {
      const offerStatus = latestOffer?.status;
      return (
        offerStatus === InventoryManagementSellerOfferStatus.BUYER_CREATED ||
        offerStatus === InventoryManagementSellerOfferStatus.BUYER_OFFERED ||
        offerStatus === InventoryManagementSellerOfferStatus.BUYER_COUNTERED
      );
    }
    return false;
  }

  get isPendingBuyerAction(): boolean {
    const latestOffer = this.latestOfferByOfferList$();
    if (latestOffer) {
      const offerStatus = latestOffer?.status;
      return (
        offerStatus === InventoryManagementSellerOfferStatus.SELLER_OFFERED ||
        offerStatus === InventoryManagementSellerOfferStatus.SELLER_COUNTERED
      );
    }
    return false;
  }

  get sellFeeTooltip() {
    return this.auctionItemSell$()?.sellFee?.fees && this.auctionItemSell$()?.sellFee?.fees?.length > 1;
  }

  get buyFeesTotalAmount() {
    return this.auctionItemSell$()?.buyFee?.total;
  }

  get sellFeesTotalAmount(): number {
    if (this.isDirty$() && this.singleListSelected$()) {
      const latestOffer = this.latestOfferByOfferList$();
      return latestOffer?.sellFee?.total;
    }
    return this.auctionItemSell$()?.sellFee?.total;
  }

  get vehicleStatusFilter() {
    return InventoryManagementAuctionItemStatus;
  }

  get isShowVehicleImages(): boolean {
    return (
      this.auctionItemSell$().auctionItemStatus !== this.vehicleStatusFilter.HOLD &&
      this.auctionItemSell$().auctionItemStatus !== this.vehicleStatusFilter.REMOVED &&
      this.auctionItemSell$().auctionItemStatus !== this.vehicleStatusFilter.EXPIRED
    );
  }

  get isReservedStatusFilter() {
    return this.isFilterByInServiceVehicles$() && this.inventoryCurrentStatus$() === StatusFilter.RESERVED_VEHICLES;
  }

  get isVehicleTypeFilterISV() {
    return this.isFilterByInServiceVehicles$();
  }

  get isSellReportingFeatureEnabled() {
    return this.launchDarklyService.getFeatureFlag('sell-saleReportsEnabled', false);
  }

  get isNotDirty() {
    return this.isDirty$() !== true;
  }

  get isEditDisabled() {
    return this.showProcessingBanner$() || this.auctionItemSell$()?.bidCount > 0;
  }

  get isOfferListSellerSelectAllEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('offerlist-seller-selectAllEnabled', true);
  }

  localizeDateAndTime(dateTime) {
    return this.dateUtilService.localizeDateAndTime(dateTime, true);
  }

  get isCustomerInfoEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-customerInfoEnabled', false);
  }

  get isReleaseProceedsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-adminReleaseProceedsEnabled', true);
  }

  get inServiceVehiclesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isReservationsEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('global-reservationsEnabled', false);
  }
  get isInspectionSeriesEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('platform-inspectionSeriesEnabled', false);
  }

  get yellowLightDisclosureCount() {
    return this.auctionItemSell$()?.disclosures?.yellowLights?.length
      ? this.auctionItemSell$().disclosures.yellowLights.length : 0;
  }

  get yellowLightDisclosureList() {
    return get(this.auctionItemSell$(), 'disclosures.yellowLights', []);
  }

  get limitedArbitration() {
    return !!this.auctionItemSell$()?.disclosures?.limitedArbitration;
  }

  get isSellVehicleCardDisclosuresEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-vehicleCardDisclosuresEnabled', false);
  }

  get isHistoricalHighEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-historicalHighEnabled', false);
  }

  get showHistoricalHigh(): boolean {
    return this.isHistoricalHighEnabled
      && this.auctionItemSell$().auctionItemStatus === this.vehicleStatusFilter.ACTIVE
      && (this.searchStatusFilter$() === StatusFilter.ACTIVE_VEHICLES
        || this.searchStatusFilter$() === StatusFilter.SHARED_VEHICLES);
  }

  get historicalHighTooltip() {
    return {
      title: this.vehicleCardTranslations.textHistoricalHigh,
      metaTitle: 'historical high',
      body: this.vehicleCardTranslations.historicalHighDescription
    };
  }

  get switchSharedWarningPositionToTopOfCard(): boolean {
    return this.showHistoricalHigh && this.offerLists$()?.length > 0;
  }
}
